import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { environment as ENV } from '../environments/environment';
import { FirebaseApp } from '@angular/fire/app';
import {
  getAnalytics,
  setUserId,
  setUserProperties
} from '@angular/fire/analytics';
import { User } from './models/user.model';
import { MultiCountryService } from './services/multi-country.service';
import { CONSTANTS } from './constants';
import { MenuOption } from './models/menu-option.model';
import { UserService } from './services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { NgClass } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { LoadingService } from './services/loading.service';
import { SpinnerComponent } from './components/spinner/spinner.component';

const LOGOUT_LANGUAGE_STORAGE_KEY = 'app.logout.language';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    SidebarComponent,
    SpinnerComponent,
    NgClass,
    RouterOutlet,
    ToastModule
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly authService = inject(MsalService);
  readonly firebaseApp = inject(FirebaseApp);
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);
  private readonly multicountryService = inject(MultiCountryService);

  user!: User;
  countryCode = 'gbr';
  menuOptions!: MenuOption[];
  menuCollapsed = false;
  showOptions = false;
  isRedirectRoute = false;
  isLogoutRoute = false;
  readonly loadingService = inject(LoadingService);

  private readonly app =
    location.hostname !== 'localhost' ? this.firebaseApp : undefined;

  ngOnInit(): void {
    const currentPath = window.location.pathname;
    this.isRedirectRoute = currentPath.endsWith('/redirect');
    this.isLogoutRoute = currentPath.endsWith('/logout');
    this.translateService.setDefaultLang(this.countryCode);
    this.translateService.use(this.countryCode);
    this.menuOptions = CONSTANTS.ALL_MENU_OPTIONS;

    if (!this.isRedirectRoute && !this.isLogoutRoute) {
      this.authService.handleRedirectObservable().subscribe({
        error: (error) => console.error('MSAL redirect handling error:', error)
      });
    }

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => this.handleMsalLogin());
  }

  private handleMsalLogin(): void {
    if (this.isLogoutRoute) {
      return;
    }

    const activeAccount =
      this.authService.instance.getActiveAccount() ??
      this.authService.instance.getAllAccounts()[0];

    if (activeAccount) {
      this.authService.instance.setActiveAccount(activeAccount);
      console.log('login successful!');
      this.user = this.userService.getMe();
      // set current user language
      this.countryCode = this.userService.getISO3CountryCode();
      this.translateService.use(this.countryCode);

      if (this.user.roles) {
        this.buildOptionsFromRolePermissions(this.user.roles);
      }

      // set data to Google Analytics
      if (location.hostname !== 'localhost') {
        this.configureGoogleAnalytics();
      }
    } else {
      console.log('need login!');
    }
  }

  private configureGoogleAnalytics(): void {
    const analytics = getAnalytics(this.app);
    setUserId(analytics, this.user.employeeId);
    setUserProperties(analytics, { employee_id: this.user.employeeId });
    setUserProperties(analytics, { app_version: ENV.appVersion });
  }

  setCollapse(isCollapse: boolean): void {
    this.menuCollapsed = !isCollapse;
  }

  /**
   * Method to get all the allowed menu options by role
   * @param roles Array of strings
   */
  async buildOptionsFromRolePermissions(roles: string[]): Promise<void> {
    try {
      const pages = await this.getPagesByRoles(roles);
      const pagesSet = new Set<string>(['/', ...pages]);
      this.menuOptions = this.menuOptions.filter((opt) =>
        pagesSet.has(opt.route)
      );
    } catch (err) {
      console.error('buildOptionsFromRolePermissions: ', err);
      this.menuOptions = CONSTANTS.DEFAULT_MENU_OPTIONS;
    }
    this.showOptions = true;
  }

  private async getPagesByRoles(roles: string[]): Promise<string[]> {
    const actionsByRole = await Promise.all(
      roles.map(async (role: string) => {
        const { allowableActions } =
          await this.multicountryService.getConfigByRole(role);
        return allowableActions;
      })
    );
    return actionsByRole.reduce((acc, next) => [...acc, ...next], []);
  }

  logOut(): void {
    sessionStorage.setItem(
      LOGOUT_LANGUAGE_STORAGE_KEY,
      this.translateService.currentLang || this.countryCode
    );
    this.userService.logout();
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
